<template>
  <LayoutContainer>
    <h1 class="mb-5 text-lg font-bold uppercase tracking-wide">
      {{ title }}
    </h1>
    <div
      class="flex flex-col items-center gap-4 rounded bg-primaryGray p-4 sm:grid sm:grid-cols-3"
    >
      <div class="text-xs max-sm:order-2 sm:col-span-2">
        <div class="grid grid-cols-2 gap-2">
          <template
            v-for="({ header, render }, index) in info"
            :key="`info-${index}`"
          >
            <div class="font-bold">{{ header }}</div>
            <div>
              <template
                v-for="(part, partIndex) in render"
                :key="`part-${index}-${partIndex}`"
              >
                <Component :is="part" />
              </template>
            </div>
          </template>
        </div>
      </div>
      <div class="max-sm:order-1">
        <div
          class="mx-auto flex aspect-square items-center rounded bg-white p-2 max-sm:w-1/2"
        >
          <NuxtLink
            v-if="logo"
            :to="brand?.website"
            :title="brand?.name"
            target="_blank"
          >
            <NuxtImg
              :src="logo"
              :alt="brand?.name"
              :width="sizes['w']"
              :height="sizes['h']"
              sizes="50vw sm:206px"
            />
          </NuxtLink>
        </div>
      </div>
    </div>

    <div v-if="applyUrl" class="mt-5">
      <NuxtLink
        class="btn inline-block px-8 py-3 text-sm font-bold"
        :to="applyUrl"
        target="_blank"
        @click="trackApply(applyUrl)"
        >Apply</NuxtLink
      >
    </div>

    <PostContent class="mt-5" :content="content" />

    <BackToTopButton />
  </LayoutContainer>
</template>

<script setup lang="ts">
import { NuxtLink } from "#components"
import type { QueryOpportunities } from "~/@types/opportunities"

const props = defineProps<{
  post: QueryOpportunities
}>()

const { post } = toRefs(props)

const dayjs = useDayjs()

const {
  title,
  content,
  logo,
  brand,
  publishedAt,
  career,
  industry,
  city,
  country,
  jobType,
  applyUrl,
} = useOpportunities(post.value)

const { name: countryName, permalink: countryPermalink } = useCountry(country)

const {
  name: cityName,
  shortName: cityShortName,
  permalink: cityPermalink,
} = useCity(city)

const { sizes } = useImageInfo(logo)

const location = cityShortName ?? cityName

const trackApply = (applyTo: string) => {
  useTrackEvent("apply", {
    brand: brand?.name,
    title,
    applyTo,
  })
}

interface Info {
  header: string
  render?: (VNode | null)[]
}

const info: Info[] = [
  {
    header: "Date",
    render: [h("span", dayjs.tz(publishedAt).format("ll"))],
  },
  {
    header: "Career",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: career?.permalink,
        },
        {
          default: () => career?.name,
        },
      ),
    ],
  },
  {
    header: "Organization",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: brand?.permalink,
        },
        {
          default: () => brand?.name,
        },
      ),
    ],
  },
  {
    header: "Industry",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: industry?.permalink,
        },
        {
          default: () => industry?.name,
        },
      ),
    ],
  },
  {
    header: "Location",
    render: [
      location
        ? h(
            NuxtLink,
            {
              class: "link city-link",
              to: cityPermalink,
            },
            {
              default: () => location,
            },
          )
        : null,
      location ? h("span", ", ") : null,
      h(
        NuxtLink,
        {
          class: "link country-link",
          to: countryPermalink,
        },
        {
          default: () => countryName,
        },
      ),
    ].filter(Boolean),
  },
  {
    header: "Type",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: jobType?.permalink,
        },
        {
          default: () => jobType?.name,
        },
      ),
    ],
  },
  {
    header: "Website",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: brand?.website,
          target: "_blank",
        },
        {
          default: () => brand?.website,
        },
      ),
    ],
  },
  {
    header: "Social",
    render: [
      h(
        NuxtLink,
        {
          class: "link",
          to: brand?.socialUrl,
          target: "_blank",
        },
        {
          default: () => brand?.socialLabel,
        },
      ),
    ],
  },
]
</script>
